import React, {useEffect, useMemo, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {ColumnDef} from 'src/ui-components/table';
import {Label} from 'src/ui-components/Label/label';
import {
    ImagePlusBlue,
    CopyLink,
    DeleteIcon,
    UploadCloudBlue,
    ChevronUpBlue,
    ChevronDownBlue,
    SocialShareIcon,
} from 'src/ui-components/Iconpack';
import DefaultImage from 'src/assets/icons/default-image.svg';
import {toast} from 'react-toastify';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'src/ui-components/Tooltip/tooltip';
import {deleteMediaFile, deleteImageMediaFile} from 'src/actions/media';
import {Progressbar} from 'src/ui-components/ProgressBar/progressbar';
import {SimpleTable} from 'src/ui-components/table/SimpleTable';
import {Button} from 'src/ui-components/Button/button';
import {encodeUrl, GetRandomPercent, TruncateTitle} from 'src/utils/helper';
import {useAppDispatch} from 'src/store';
import DeleteFile from 'src/components/delete-file/deleteFile';
import tracker from 'src/lib/analytics/tracker';
import {EventNames} from 'src/lib/analytics/events';
import {Row} from '@tanstack/react-table';
import {Dialog, DialogContent, DialogTrigger} from '../../ui-components/Modal';
import SocialShare from '../social-share/SocialShare';
import Tracker from 'src/lib/analytics/tracker';
import {getCorrectedName} from '../../utils/media';
import {FeatureFlags} from '../../contexts/FeatureFlags';

interface TableInterface {
    files: any;
    onFileDrop: (data: any) => void;
    onFileDelete: (fileUid: string) => void;
    removeFromTable: (fileName: string) => void;
    onFileChildDelete: (parentUid: string, childUid: string) => void;
    orgId: string;
    dragDiv: boolean;
    selectAll?: boolean;
    isAllSelected?: (selected: boolean) => void;
    getSelectedData?: (selectedUids: string[]) => void;
    clearSelection?: boolean;
    resetClearState?: () => void;
}

export default function TableListView({
    files,
    onFileDrop,
    orgId,
    onFileDelete,
    onFileChildDelete,
    dragDiv,
    removeFromTable,
    selectAll,
    isAllSelected,
    getSelectedData,
    clearSelection,
    resetClearState,
}: TableInterface) {
    const [dragOver, setDragOver] = useState(false);
    const [deleteFileUid, setDeleteFileUid] = useState('');
    const [deleteFileName, setDeleteFileName] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);
    const [childUid, setChildUid] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<string[]>();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        onFileDrop(droppedFiles);
    };

    const copyText = (cdnUrl: string, thumbnailUrl: string | undefined, rowDepth: number | undefined) => {
        if (rowDepth && rowDepth > 0 && thumbnailUrl) {
            tracker.trackEvent(EventNames.TRANSFORMATION_COPIED);
            return navigator.clipboard.writeText(encodeUrl(thumbnailUrl));
        }
        tracker.trackEvent(EventNames.IMAGE_COPIED);
        return navigator.clipboard.writeText(encodeUrl(cdnUrl));
    };

    const getFileSize = (size: number | undefined) => {
        if (size) {
            const sizeInKB = size / 1024;
            const sizeInMB = sizeInKB / 1024;
            if (sizeInKB < 1000) {
                return `${sizeInKB.toFixed(2)} KB`;
            } else {
                return `${sizeInMB.toFixed(2)} MB`;
            }
        }
        return 'N/A';
    };
    const deleteFile = async () => {
        if (childUid && childUid !== '') {
            await dispatch(deleteImageMediaFile({imageUid: childUid, mediaFileUid: deleteFileUid, orgUid: orgId}));
            onFileChildDelete(deleteFileUid, childUid);
        } else {
            await dispatch(deleteMediaFile({orgUid: orgId, mediaFileUid: deleteFileUid}));
            onFileDelete(deleteFileUid);
        }
        setChildUid('');
        setDeleteFileName('');
        setDeleteFileUid('');
        setDeleteModal(false);
    };

    const suspendDeleteModal = () => {
        setDeleteModal(false);
        setDeleteFileUid('');
        setDeleteFileName('');
    };

    const goToImageDetails = (row: Row<any>) => {
        const {original, depth, index} = row;
        const baseRoute = `/${orgId}/image-details`;

        let route = `${baseRoute}/${original.file_uid}`;
        if (depth !== 0) {
            route = `${baseRoute}/${original.parent_uid}?transformedUid=${original.uid}&versionNumber=${index + 1}`;
        }
        navigate(route);
    };

    useEffect(() => {
        if (deleteFileUid !== '') {
            setDeleteModal(true);
        } else {
            removeFromTable(deleteFileName);
        }
    }, [deleteFileName]);

    const toggleCheckbox = (fileUid: string) => {
        setSelectedFiles((prevSelectedFiles) => {
            const isSelected = prevSelectedFiles?.includes(fileUid);
            if (isSelected) {
                return prevSelectedFiles?.filter((uid) => uid !== fileUid);
            } else {
                return [...(prevSelectedFiles || []), fileUid];
            }
        });
    };

    useEffect(() => {
        if (selectAll) {
            const allFileUids = files.map((item: {file_uid: string}) => {
                return item.file_uid;
            });
            setSelectedFiles(allFileUids);
        } else {
            setSelectedFiles([]);
        }
    }, [selectAll]);

    useEffect(() => {
        if (isAllSelected) {
            isAllSelected(files.length === selectedFiles?.length);
        }
        if (getSelectedData) {
            if (selectedFiles?.length) {
                getSelectedData(selectedFiles);
            }
        }
    }, [selectedFiles]);

    useEffect(() => {
        if (clearSelection) {
            setSelectedFiles([]);
            if (resetClearState) {
                resetClearState();
            }
        }
    }, [clearSelection]);

    const columns = useMemo<ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'checkbox',
                header: '',
                cell: ({row}) => (
                    <input
                        type='checkbox'
                        checked={selectedFiles?.includes(row.original.file_uid)}
                        onChange={() => toggleCheckbox(row.original.file_uid)}
                        className={`flex ${row.depth === 0 ? '' : 'hidden'}`}
                    />
                ),
            },
            {
                accessorKey: 'thumbnail',
                header: '',
                cell: ({row}) => (
                    <div
                        className={`relative flex cursor-pointer items-center justify-center gap-1`}
                        style={{left: `${row.depth * 5}rem`}}>
                        <img
                            className='h-11 w-11'
                            src={
                                row.original.thumbnail_url !== ''
                                    ? row.original.title.toLowerCase().endsWith('.svg') ||
                                      row.original.title.toLowerCase().endsWith('.gif')
                                        ? encodeUrl(row.original.cdn_url)
                                        : encodeUrl(row.original.thumbnail_url)
                                    : DefaultImage
                            }
                            alt={row.original.thumbnail_url}
                            onClick={() => goToImageDetails(row)}
                        />
                    </div>
                ),
            },
            {
                accessorKey: 'name',
                header: 'Name',
                cell: ({row}) => (
                    <div className={`relative`} style={{left: `${row.depth * 5}rem`}}>
                        <div className='flex w-[300px] items-center justify-between'>
                            <span
                                className='cursor-pointer text-[14px] font-[500] text-[#0C111D]'
                                onClick={() => goToImageDetails(row)}>
                                {TruncateTitle(
                                    getCorrectedName(row.original.title, row.depth > 0 ? row.original.type : null),
                                    20,
                                )}
                            </span>
                            {row.subRows.length !== 0 && (
                                <Button
                                    className='border border-[#D1E9FF] bg-[#EFF8FF] px-[6px] py-[2px] text-[12px] font-[500] text-[#155EEF] hover:bg-[#D1E9FF]'
                                    {...{
                                        onClick: row.getToggleExpandedHandler(),
                                        style: {cursor: 'pointer'},
                                    }}>
                                    {' '}
                                    {row.subRows.length <= 1
                                        ? `+${row.subRows.length} transform`
                                        : `+${row.subRows.length} transforms`}
                                    {row.getIsExpanded() ? <ChevronUpBlue /> : <ChevronDownBlue />}
                                </Button>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: 'uploadStatus',
                header: 'Upload Status',
                cell: ({row}) => (
                    <div className='flex items-center'>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    {!row.original.actionStatus && (
                                        <div>
                                            <Label variant='success'>Successful</Label>
                                        </div>
                                    )}
                                    {row.original.actionStatus === 'uploading' && (
                                        <div className='items-center justify-center gap-[2px]'>
                                            <Label>Uploading</Label>
                                            <Progressbar
                                                value={GetRandomPercent()}
                                                indicator='uploading'
                                                className='w-[80px] bg-[#EAECF5]'
                                            />
                                        </div>
                                    )}
                                    {row.original.actionStatus === 'uploaded' && (
                                        <Label variant='success'>Successful</Label>
                                    )}
                                    {row.original.actionStatus === 'error' && (
                                        <Label variant='error'>{row.original.error.slice(0, 15)}</Label>
                                    )}
                                </TooltipTrigger>
                                {row.original.error ? (
                                    <TooltipContent>
                                        <p>{row.original.error}</p>
                                        <TooltipArrow />
                                    </TooltipContent>
                                ) : null}
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                ),
            },
            {
                accessorKey: 'size',
                header: 'Size',
                cell: ({row}) => (
                    <div className='flex items-center'>
                        <span className='text-[14px] font-[400] text-[#344054]'>
                            {getFileSize(row.original.file_size)}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'dimensions',
                header: 'Dimensions',
                cell: ({row}) => (
                    <div className='flex items-center'>
                        <span className='text-[14px] font-[400] text-[#344054]'>
                            {row.original.width ? `${row.original.width}X${row.original.height}` : ''}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'transform',
                header: '',
                cell: ({row}) => (
                    <Link
                        to={
                            row.depth === 0
                                ? `/transformation/${row.original.file_uid}`
                                : `/transformation/${row.original.parent_uid}?transformId=${row.original.uid}`
                        }
                        className='flex cursor-pointer items-center gap-[0.5rem]'>
                        <ImagePlusBlue />
                        <span className='text-[14px] font-[600] text-[#155EEF]'>Transform</span>
                    </Link>
                ),
            },
            {
                accessorKey: 'pixelstudio',
                header: '',
                cell: ({row}) => (
                    <FeatureFlags>
                        <Link
                            to={
                                row.depth === 0
                                    ? `/pixelstudio?imageUid=${row.original.file_uid}`
                                    : `/transformation/${row.original.parent_uid}?transformId=${row.original.uid}`
                            }
                            className='flex cursor-pointer items-center gap-[0.5rem]'>
                            <span className='text-[14px] font-[600] text-[#155EEF]'>Studio</span>
                        </Link>
                    </FeatureFlags>
                ),
            },
            {
                accessorKey: 'copy',
                header: '',
                cell: ({row}) => (
                    <div
                        className='flex cursor-pointer items-center'
                        onClick={() => {
                            copyText(row.original.cdn_url, row.original.thumbnail_url, row.depth).then(() => {
                                toast.success('Image Url Copied');
                            });
                        }}>
                        {!row.original.error ? (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <CopyLink />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Copy Image Url</p>
                                        <TooltipArrow />
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : null}
                    </div>
                ),
            },
            {
                accessorKey: 'share',
                header: '',
                cell: ({row}) => (
                    <div className='flex cursor-pointer items-center'>
                        {!row.original.error ? (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Dialog>
                                            <DialogTrigger>
                                                <div
                                                    className='cursor-pointer pr-[12px]'
                                                    onClick={() => Tracker.trackEvent(EventNames.SHARE_IMAGE_CLICKED)}>
                                                    <SocialShareIcon height={20} width={20} />
                                                </div>
                                            </DialogTrigger>
                                            <DialogContent className='w-[333px] md:w-[1063px]'>
                                                <SocialShare imageUrl={row.original.cdn_url} />
                                            </DialogContent>
                                        </Dialog>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Share Image</p>
                                        <TooltipArrow />
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : null}
                    </div>
                ),
            },
            {
                accessorKey: 'delete',
                header: '',
                cell: ({row}) => (
                    <div
                        className='flex cursor-pointer items-center'
                        onClick={() => {
                            setChildUid(row.depth > 0 ? row.original.uid : '');
                            setDeleteFileUid(row.depth === 0 ? row.original.file_uid : row.original.parent_uid);
                            setDeleteFileName(TruncateTitle(row.original.title, 20));
                        }}>
                        <DeleteIcon />
                    </div>
                ),
            },
        ],
        [selectedFiles],
    );

    return (
        <div>
            <SimpleTable
                columns={columns}
                data={files.map((item: any, index: number) => {
                    return {...item};
                })}
                loading={false}
                roundedTable={false}
            />
            {dragDiv ? (
                <div
                    className={`fixed bottom-0 left-[320px] z-10 flex w-[calc(100%-320px)] flex-col items-center 
                        justify-center bg-gradient-to-t from-[#D9D9D9] from-0% to-[#155EEF] opacity-95 shadow-[#C1C1C1]`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}>
                    <div className='flex items-center justify-center pt-12'>
                        <UploadCloudBlue />
                    </div>
                    <div className='my-10 flex flex-col items-center justify-center rounded-[16px] bg-[#155EEF] px-14 py-5 text-[18px] text-[#FFFFFF]'>
                        <span className='font-[700] '>Drag and drop files or folder to upload directly</span>
                        <span className='font-[400]'>SVG, PNG, JPG or GIF (max. 16MB)</span>
                    </div>
                </div>
            ) : null}
            <DeleteFile
                fileName={deleteFileName}
                onClose={suspendDeleteModal}
                onConfirm={deleteFile}
                open={deleteModal}
            />
        </div>
    );
}
